import { ArrowUpCircle, Network, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { Badge, type BadgeProps } from '@/components/ui/Badge'
import { Button, type ButtonProps } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { Switch } from '@/components/ui/Switch'
import { absoluteTime, relativeTime } from '@/lib'
import type { GameVersion, Platform, ReleaseEvent, ReleaseStatus, VersionFlags } from '@/models'

const PLATFORM_LABEL: Record<Platform, string> = { ios: 'iOS', android: 'Android' }

const STATUS_VARIANT: Record<ReleaseStatus, NonNullable<BadgeProps['variant']>> = {
  draft: 'neutral',
  in_review: 'warning',
  rejected: 'danger',
  released: 'success',
  archived: 'neutral',
}

const STATUS_LABEL: Record<ReleaseStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  rejected: 'Rejected',
  released: 'Released',
  archived: 'Archived',
}

const STATUS_DESCRIPTION: Record<ReleaseStatus, string> = {
  draft: 'Still being prepared. Not yet submitted to the App Store.',
  in_review: 'Submitted and awaiting App Store review of the release.',
  rejected: 'The App Store review did not pass. Fix the issues and resubmit.',
  released: 'Approved and live for players on the App Store.',
  archived: 'Retired release, superseded by a newer version.',
}

/* Lifecycle buttons. Order here is the order they render in. `to` is the
 * status the event moves the release into (used in the confirm prompt). */
const EVENT_ACTIONS: {
  event: ReleaseEvent
  label: string
  variant: ButtonProps['variant']
  to: ReleaseStatus
}[] = [
  { event: 'submit', label: 'Submit for review', variant: 'default', to: 'in_review' },
  { event: 'approve', label: 'Approve', variant: 'default', to: 'released' },
  { event: 'reject', label: 'Reject', variant: 'destructive', to: 'rejected' },
  { event: 'archive', label: 'Archive', variant: 'outline', to: 'archived' },
]

const FLAGS: {
  key: keyof VersionFlags
  label: string
  description: string
  icon: typeof ShieldAlert
}[] = [
  {
    key: 'maintain',
    label: 'Maintenance Mode',
    description: 'Block sign-in and show maintenance screen.',
    icon: ShieldAlert,
  },
  {
    key: 'forceUpdate',
    label: 'Force Client Update',
    description: 'Require older clients to update before connecting.',
    icon: ArrowUpCircle,
  },
  {
    key: 'fallbackNetConfig',
    label: 'Fallback Gateway',
    description: 'Route network requests through backup gateway.',
    icon: Network,
  },
]

interface VersionSummaryCardProps {
  version: GameVersion
  onFlagChange: (key: keyof VersionFlags, value: boolean) => void
}

interface VersionSummaryHeaderProps {
  version: GameVersion
  canEdit?: boolean
  onTransition?: (event: ReleaseEvent) => void
}

export function VersionSummaryHeader({
  version,
  canEdit = false,
  onTransition,
}: VersionSummaryHeaderProps) {
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-fg text-xl font-bold tracking-tight tabular-nums sm:text-2xl">
              v{version.version}
            </span>
            <Badge variant="outline" size="tag">
              {PLATFORM_LABEL[version.platform]}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge dot variant={STATUS_VARIANT[version.status]}>
              {STATUS_LABEL[version.status]}
            </Badge>
            <span className="text-fg-subtle text-xs">{STATUS_DESCRIPTION[version.status]}</span>
          </div>
        </div>

        <div className="text-fg-subtle flex items-center gap-4 text-xs">
          <div className="text-right leading-tight">
            <span className="text-fg-subtle block">Updated {relativeTime(version.updatedAt)}</span>
            <span className="text-fg-subtle block text-xs tabular-nums">
              {absoluteTime(version.updatedAt)}
            </span>
          </div>
        </div>
      </div>

      <VersionStatusActions version={version} canEdit={canEdit} onTransition={onTransition} />
    </Card>
  )
}

interface VersionStatusActionsProps {
  version: GameVersion
  canEdit?: boolean
  onTransition?: (event: ReleaseEvent) => void
}

/* Release lifecycle buttons. Rendered as a footer inside the current-version
 * card. Every transition goes through a confirm dialog first. */
export function VersionStatusActions({
  version,
  canEdit = false,
  onTransition,
}: VersionStatusActionsProps) {
  const [pending, setPending] = useState<(typeof EVENT_ACTIONS)[number] | null>(null)

  if (!canEdit) return null
  const actions = EVENT_ACTIONS.filter(({ event }) => version.permittedEvents.includes(event))
  if (actions.length === 0) return null

  const confirm = () => {
    if (pending) onTransition?.(pending.event)
    setPending(null)
  }

  return (
    <div className="border-border flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
      {actions.map((action) => (
        <Button
          key={action.event}
          size="sm"
          variant={action.variant}
          onClick={() => setPending(action)}
        >
          {action.label}
        </Button>
      ))}

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        className="w-[420px]"
      >
        <DialogHeader>
          <DialogTitle>{pending?.label}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-fg-muted text-sm">
            Move <span className="text-fg font-medium tabular-nums">v{version.version}</span> from{' '}
            <span className="text-fg font-medium">{STATUS_LABEL[version.status]}</span> to{' '}
            <span className="text-fg font-medium">
              {pending ? STATUS_LABEL[pending.to] : ''}
            </span>
            ?
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button variant={pending?.variant} onClick={confirm}>
            {pending?.label}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}

export function VersionPolicyCard({ version, onFlagChange }: VersionSummaryCardProps) {
  return (
    <div className="space-y-3">
      {FLAGS.map(({ key, label, description, icon: Icon }) => (
        <label
          key={key}
          className="group border-border bg-bg-faint hover:border-border-strong hover:bg-bg-elev flex cursor-pointer items-start justify-between gap-3 rounded-xl border p-3.5 shadow-2xs transition-colors duration-100 ease-out"
        >
          <div className="flex min-w-0 items-start gap-3">
            <span
              aria-hidden
              className="border-border bg-bg-elev text-fg-muted group-hover:text-fg mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg border transition-colors duration-100 ease-out"
            >
              <Icon className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-fg block text-xs font-semibold">{label}</span>
              <span className="text-fg-subtle mt-0.5 block text-xs">{description}</span>
            </div>
          </div>
          <Switch
            checked={version.flags[key]}
            onCheckedChange={(checked) => onFlagChange(key, checked)}
            className="mt-0.5 shrink-0"
          />
        </label>
      ))}
    </div>
  )
}

export function VersionSummaryCard({ version, onFlagChange }: VersionSummaryCardProps) {
  return (
    <div className="space-y-4">
      <VersionSummaryHeader version={version} />
      <VersionPolicyCard version={version} onFlagChange={onFlagChange} />
    </div>
  )
}
